import React, { useState } from 'react';
import { useUpdateZonesBulk } from '../hooks/useZonesBulk';
import { useToastContext } from '../contexts/ToastContext';

interface BulkUpdateZonesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: string[];
}

const BulkUpdateZonesModal: React.FC<BulkUpdateZonesModalProps> = ({
  isOpen,
  onClose,
  selectedIds,
}) => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const updateZonesBulkMutation = useUpdateZonesBulk();
  const [field, setField] = useState<'theme' | 'type' | 'nombreEtagere'>('theme');
  const [theme, setTheme] = useState('');
  const [type, setType] = useState('');
  const [nombreEtagere, setNombreEtagere] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updates: any = {};
    if (field === 'theme' && theme) updates.theme = theme;
    else if (field === 'type' && type) updates.type = type;
    else if (field === 'nombreEtagere') updates.nombreEtagere = nombreEtagere;

    try {
      await updateZonesBulkMutation.mutateAsync({
        zoneIds: selectedIds,
        updates,
      });
      showSuccess('Zones modifiées', `${selectedIds.length} zone(s) modifiée(s) avec succès.`);
      onClose();
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  ✏️ Modifier {selectedIds.length} zone(s)
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Champ à modifier
                  </label>
                  <select
                    value={field}
                    onChange={(e) => setField(e.target.value as any)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="theme">Thème</option>
                    <option value="type">Type</option>
                    <option value="nombreEtagere">Nombre d'étagères</option>
                  </select>
                </div>

                {field === 'theme' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nouveau thème
                    </label>
                    <input
                      type="text"
                      value={theme}
                      onChange={(e) => setTheme(e.target.value)}
                      placeholder="Ex: Jeux de société, Matériel créatif"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Séparez plusieurs thèmes par des virgules
                    </p>
                  </div>
                )}

                {field === 'type' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de zone
                    </label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Sélectionner un type</option>
                      <option value="Armoire">Armoire</option>
                      <option value="Étagère">Étagère</option>
                      <option value="Bureau">Bureau</option>
                      <option value="Placard">Placard</option>
                      <option value="Meuble">Meuble</option>
                      <option value="Zone ouverte">Zone ouverte</option>
                    </select>
                  </div>
                )}

                {field === 'nombreEtagere' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre d'étagères
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={nombreEtagere}
                      onChange={(e) => setNombreEtagere(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                )}

                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    ⚠️ Cette action modifiera les {selectedIds.length} zones sélectionnées.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={updateZonesBulkMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {updateZonesBulkMutation.isPending ? 'Modification...' : 'Modifier'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateZonesModal;

